import React, { FunctionComponent } from "react";
import { useHistory } from "react-router-dom";
import { FormikHelpers } from "formik";
import { SignUp } from "./component";
import { useFiubaLogin, useSaveApplicant, useSharedSettings, useTranslations } from "$hooks";
import { hasUniqueValues } from "$models/hasUniqueValues";
import { RoutesBuilder } from "$models/RoutesBuilder";
import { IApplicantSignUpFormValues, IApplicantSignUpTranslations } from "./interfaces";
import { useShowError } from "$hooks/snackbar";
import { formErrorHandlers } from "$models/errorHandlers/formErrorHandlers";
import { FiubaAuthenticationErrorHandler } from "$models/errorHandlers";
import { saveApplicantArguments } from "$models/MutationArguments";
import { Window } from "$components/Window";

export const SignUpContainer: FunctionComponent<ISignUpProps> = ({ searchQuery }) => {
  const history = useHistory();
  const { saveApplicant } = useSaveApplicant();
  const { login } = useFiubaLogin();
  const showError = useShowError();
  const translations = useTranslations<IApplicantSignUpTranslations>("applicantSignUp");
  const acceptanceCriteria = useSharedSettings()?.applicantSignUpAcceptanceCriteria;

  const validateForm = (values: IApplicantSignUpFormValues) => {
    const selectedCodes = values.careers.map(career => career.careerCode);
    if (hasUniqueValues(selectedCodes)) {
      return "No se pueden repetir carreras";
    }
    if (selectedCodes.length === 0) {
      return "Debes elegir como mínimo una carrera";
    }
  };

  const queryParams = new URLSearchParams(searchQuery);
  const signUpToken = sessionStorage.getItem("signUpToken") || queryParams.get("signUpToken");
  if (!signUpToken) {
    history.push(RoutesBuilder.public.internalServerError());
  }

  const onSubmit = async (
    { _form, user, ...applicantValues }: IApplicantSignUpFormValues,
    { setErrors }: FormikHelpers<IApplicantSignUpFormValues>
  ) => {
    const saveApplicantResult = await saveApplicant({
      variables: saveApplicantArguments({ user, ...applicantValues }),
      errorHandlers: formErrorHandlers(showError)({
        UserEmailAlreadyExistsError: () => showError({ message: "Este email ya existe" }),
        ApplicantAlreadyExistsError: () => setErrors({ user: { dni: "Este dni ya existe" } }),
        ...FiubaAuthenticationErrorHandler(showError)
      })
    });
    if (saveApplicantResult.error) return;

    const tokenPayload = JSON.parse(atob(user.signUpToken.split(".")[1]));
    const loginResult = await login({
      variables: { sub: tokenPayload.sub },
      errorHandlers: { defaultHandler: () => history.push(RoutesBuilder.public.login()) }
    });
    if (loginResult.error) return;
    history.push(RoutesBuilder.applicant.editMyProfile());
  };

  return (
    <Window withoutNavBar loading={!translations}>
      {translations && (
        <SignUp
          translations={translations}
          validateForm={validateForm}
          onSubmit={onSubmit}
          // initialDni={new URLSearchParams(searchQuery).get("dni")}
          initialName={new URLSearchParams(searchQuery).get("nombre")}
          initialSurname={new URLSearchParams(searchQuery).get("apellido")}
          signUpToken={signUpToken as string}
          acceptanceCriteria={acceptanceCriteria}
        />
      )}
    </Window>
  );
};

interface ISignUpProps {
  searchQuery: string;
}
